export const NAV_LINKS = [
  { label: "About", href: "/about" },
  { label: "What We Solve", href: "/what-we-solve" },
  { label: "How We Work", href: "/how-we-work" },
  { label: "Our Work", href: "/our-work" },
  { label: "Switchboard", href: "/insights" },
];

export const PANEL_LINKS = [
  ...NAV_LINKS,
  { label: "Contact", href: "/contact" },
];

export const GROWTH_SITUATIONS = [
  {
    id: "stalled",
    number: "01",
    title: "Growth Has Stalled",
    blurb:
      "The business is active, but revenue, demand, market share or profitability has stopped moving meaningfully.",
    symptoms: [
      "Growth has slowed or plateaued",
      "Marketing is not translating into business",
      "Sales or distribution is underperforming",
      "Profitability is weakening",
    ],
    cta: "Explore Growth Has Stalled",
    href: "/what-we-solve/growth-has-stalled",
    condition: "compressed",
  },
  {
    id: "scale",
    number: "02",
    title: "Ready to Scale",
    blurb:
      "The opportunity is visible. The systems, market focus and operating discipline required to capture it are not yet ready.",
    symptoms: [
      "Expansion is outpacing the organisation",
      "The business remains too dependent on the founder",
      "Teams are not aligned around one growth agenda",
      "Regional success is not translating into wider scale",
    ],
    cta: "Explore Ready to Scale",
    href: "/what-we-solve/ready-to-scale",
    condition: "expanding",
  },
  {
    id: "transform",
    number: "03",
    title: "Reposition & Transform",
    blurb:
      "The current brand, proposition, business model or organisation can no longer take the company where it needs to go.",
    symptoms: [
      "The brand has lost relevance or distinction",
      "The proposition is unclear",
      "The current model is constraining growth",
      "The business requires a broader transformation",
    ],
    cta: "Explore Reposition & Transform",
    href: "/what-we-solve/reposition-or-transform",
    condition: "transition",
  },
  {
    id: "value",
    number: "04",
    title: "Create Enterprise Value",
    blurb:
      "Growth must become more profitable, more predictable and more valuable to the business, board or investor.",
    symptoms: [
      "Revenue is growing without sufficient value creation",
      "Leadership needs a credible growth plan",
      "Investors need a clearer strategic roadmap",
      "Profitability and business strength must improve together",
    ],
    cta: "Explore Create Enterprise Value",
    href: "/what-we-solve/create-enterprise-value",
    condition: "consolidating",
  },
];

// The SWITCH model, the four moves (source: Homepage Final Content).
export const MODEL_STEPS = [
  { n: "01", title: "Diagnose before prescribing", desc: "We look beyond the visible symptom to identify the real constraint affecting growth." },
  { n: "02", title: "Build strategy around execution", desc: "The strategy accounts for the people, economics, capabilities, systems and decisions required to make it happen." },
  { n: "03", title: "Bring the right expertise around one agenda", desc: "The problem determines which disciplines come together, not the service structure of the company advising you." },
  { n: "04", title: "Stay through implementation", desc: "We work with leadership, internal teams and existing partners until the agreed priorities move into action." },
];

export const OPERATING_PRINCIPLES = [
  {
    title: "Diagnose before prescribing",
    desc: "Find the real constraint, not merely the most visible symptom.",
  },
  {
    title: "Build strategy around execution",
    desc: "Make decisions that account for people, capability, systems, economics and the realities of implementation.",
  },
  {
    title: "Bring the right expertise around one agenda",
    desc: "Connect the disciplines affecting growth instead of allowing each one to solve a different version of the problem.",
  },
];

export const DISCIPLINES = [
  "Strategy",
  "Brand",
  "Marketing",
  "Sales",
  "Technology",
  "Operations",
  "Finance",
  "Organisation",
];

export const CAPABILITIES = [
  {
    title: "Business Strategy & Transformation",
    desc: "Clarifying where growth will come from and what the business must change to capture it.",
  },
  {
    title: "Brand, Marketing & Digital",
    desc: "Building relevance, differentiation, demand and a more effective route from attention to business.",
  },
  {
    title: "Sales & Distribution",
    desc: "Improving market access, channel performance, conversion and last-mile execution.",
  },
  {
    title: "Technology, Products & Automation",
    desc: "Using technology to strengthen visibility, decisions, experiences, processes and scale.",
  },
  {
    title: "Consumer & Market Research",
    desc: "Replacing assumptions with evidence about people, categories, demand and behaviour.",
  },
  {
    title: "Finance & Commercial Modelling",
    desc: "Connecting growth ambition with economics, profitability, investment and value creation.",
  },
  {
    title: "Creative & Communication",
    desc: "Turning strategy into ideas and experiences people can recognise, understand and act on.",
  },
  {
    title: "Process, Systems & Organisation",
    desc: "Building the operating discipline and capability required to sustain execution.",
  },
];

// Featured work uses explicit, clearly-labelled placeholders. No invented
// figures, brand names or results, replaced from the CMS once approved.
export const SELECTED_WORK = [
  {
    id: "case-1",
    image: "/assets/work-1.jpg",
    outcome: "[Outcome headline]",
    descriptor: "[Business descriptor]",
    situation: "[Growth situation]",
    summary: "[Intervention summary]",
    lead: true,
  },
  {
    id: "case-2",
    image: "/assets/work-2.jpg",
    outcome: "[Outcome headline]",
    descriptor: "[Business descriptor]",
    situation: "[Growth situation]",
    summary: "[Intervention summary]",
    lead: false,
  },
  {
    id: "case-3",
    image: "/assets/work-3.jpg",
    outcome: "[Outcome headline]",
    descriptor: "[Business descriptor]",
    situation: "[Growth situation]",
    summary: "[Intervention summary]",
    lead: false,
  },
];

export const SELECTED_INSIGHTS = [
  {
    id: "insight-1",
    format: "[Article / Conversation]",
    title: "[Insight title]",
    summary: "[One-sentence value statement]",
    topic: "[Topic]",
    meta: "[Reading time]",
    cta: "Read the Article",
    lead: true,
  },
  {
    id: "insight-2",
    format: "[Framework / Diagnostic tool]",
    title: "[Insight title]",
    summary: "[One-sentence value statement]",
    topic: "[Topic]",
    meta: "[Completion time]",
    cta: "Explore the Framework",
    lead: false,
  },
  {
    id: "insight-3",
    format: "[Report / Video]",
    title: "[Insight title]",
    summary: "[One-sentence value statement]",
    topic: "[Topic]",
    meta: "[Gated]",
    cta: "Download the Report",
    lead: false,
  },
  {
    id: "insight-4",
    format: "[Article / Conversation]",
    title: "[Insight title]",
    summary: "[One-sentence value statement]",
    topic: "[Topic]",
    meta: "[Reading time]",
    cta: "Read the Article",
    lead: false,
  },
  {
    id: "insight-5",
    format: "[Framework / Diagnostic tool]",
    title: "[Insight title]",
    summary: "[One-sentence value statement]",
    topic: "[Topic]",
    meta: "[Completion time]",
    cta: "Explore the Framework",
    lead: false,
  },
  {
    id: "insight-6",
    format: "[Report / Video]",
    title: "[Insight title]",
    summary: "[One-sentence value statement]",
    topic: "[Topic]",
    meta: "[Gated]",
    cta: "Download the Report",
    lead: false,
  },
];

export const FIND_SWITCH_PROMPTS = [
  "Growth has plateaued despite higher marketing spends",
  "We are expanding, but margins are falling",
  "Our teams are busy but not aligned",
  "The business is too dependent on the founder",
];

export const FOOTER_NAV = [
  { label: "What We Solve", href: "/what-we-solve" },
  { label: "How We Work", href: "/how-we-work" },
  { label: "Our Work", href: "/our-work" },
  { label: "About", href: "/about" },
  { label: "Switchboard", href: "/insights" },
  { label: "Find Your Switch", href: "/find-your-switch" },
  { label: "Contact", href: "/contact" },
  { label: "Careers", href: "/careers" },
];

export const FOOTER_LEGAL = [
  { label: "Privacy Policy", href: "/legal#privacy" },
  { label: "Terms of Use", href: "/legal#terms" },
  { label: "Cookie Policy", href: "/legal#cookies" },
];

// Add the real profile URLs to re-enable each icon. Entries without a real
// URL are filtered out so the footer never renders a broken link.
const SOCIAL_LINKS = [
  { label: "LinkedIn", href: "" },
  { label: "YouTube", href: "" },
  { label: "Instagram", href: "" },
];
export const FOOTER_SOCIAL = SOCIAL_LINKS.filter((s) => s.href && s.href !== "#");

export const CONTACT_EMAIL = "hello@switchnow.in";
